import { Helmet } from 'react-helmet-async';
// @mui
import { Box, Divider, Link, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
// components
import Iconify from '../../components/iconify';
import MEITDLogo from '../../assets/logo.png';
import { MAP_VIEWBOX, DIVISION_PATHS } from '../../components/sarawak-map/divisionPaths';
//
import AuthLoginForm from './AuthLoginForm';

// ----------------------------------------------------------------------
// Official sign-in for the MEITD Manpower Intelligence Platform.
// Split layout: institutional navy brand panel (with the accurate Sarawak
// division map as a watermark) + a clean, formal credential panel.
// ----------------------------------------------------------------------

const NAVY = '#141929';

const HIGHLIGHTS = [
  ['mdi:map-marker-radius', 'Talent demand, gap and wages across all 12 Sarawak divisions'],
  ['mdi:chart-timeline-variant-shimmer', 'IO-model workforce projections to 2030, aligned to PCDS 2030'],
  ['mdi:shield-lock', 'Role-based access over governed EY sector data submissions'],
];

export default function Login() {
  const year = new Date().getFullYear();

  return (
    <>
      <Helmet>
        <title>Sign in | MEITD Manpower Intelligence Platform</title>
      </Helmet>

      <Stack direction="row" sx={{ minHeight: '100vh', bgcolor: 'background.paper' }}>
        {/* ── Brand panel ─────────────────────────────────────────── */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            width: { md: '52%', lg: '56%' },
            p: { md: 6, lg: 8 },
            color: 'common.white',
            position: 'relative',
            overflow: 'hidden',
            background: `linear-gradient(160deg, ${NAVY} 0%, #1B2340 60%, #232C4E 100%)`,
          }}
        >
          {/* Sarawak map watermark */}
          <Box
            component="svg"
            viewBox={MAP_VIEWBOX}
            sx={{
              position: 'absolute',
              right: -120,
              bottom: -60,
              width: '110%',
              opacity: 0.14,
              pointerEvents: 'none',
            }}
          >
            {Object.entries(DIVISION_PATHS).map(([name, d]) => (
              <path key={name} d={d} fill="#8E9AC0" stroke={NAVY} strokeWidth={2} />
            ))}
          </Box>

          <Stack direction="row" alignItems="center" spacing={2} sx={{ position: 'relative' }}>
            <Box component="img" src={MEITDLogo} alt="MEITD" sx={{ height: 84, objectFit: 'contain' }} />
          </Stack>

          <Stack spacing={2.5} sx={{ position: 'relative', mt: { md: 8, lg: 12 }, maxWidth: 480 }}>
            <Typography variant="overline" sx={{ color: alpha('#FFFFFF', 0.6), letterSpacing: '0.14em' }}>
              Government of Sarawak · Official System
            </Typography>
            <Typography variant="h3" sx={{ lineHeight: 1.25 }}>
              Manpower Intelligence Platform
            </Typography>
            <Typography variant="body1" sx={{ color: alpha('#FFFFFF', 0.72) }}>
              Ministry of Education, Innovation and Talent Development — evidence for Sarawak&apos;s
              talent decisions, from live sector submissions to the 2030 horizon.
            </Typography>

            <Divider sx={{ borderColor: alpha('#FFFFFF', 0.16), my: 1 }} />

            <Stack spacing={1.75}>
              {HIGHLIGHTS.map(([icon, text]) => (
                <Stack key={icon} direction="row" spacing={1.5} alignItems="flex-start">
                  <Iconify icon={icon} width={20} sx={{ mt: 0.25, color: '#CF6B77', flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: alpha('#FFFFFF', 0.78) }}>
                    {text}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>

          <Box sx={{ flexGrow: 1 }} />

          <Typography variant="caption" sx={{ position: 'relative', color: alpha('#FFFFFF', 0.45) }}>
            © {year} Government of Sarawak · Ministry of Education, Innovation and Talent Development
          </Typography>
        </Box>

        {/* ── Credential panel ────────────────────────────────────── */}
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{ flex: 1, px: { xs: 3, sm: 6 }, py: 6, bgcolor: 'background.paper' }}
        >
          <Stack sx={{ width: 1, maxWidth: 420 }} spacing={3}>
            <Box component="img" src={MEITDLogo} alt="MEITD" sx={{ height: 64, objectFit: 'contain', display: { md: 'none' }, alignSelf: 'flex-start' }} />

            <Stack spacing={0.5}>
              <Typography variant="h4">Sign in</Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                MEITD Manpower Intelligence Platform
              </Typography>
            </Stack>

            <AuthLoginForm />

            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Access is provisioned by the MEITD system administrator. If you require an account or
              have lost access, contact your ministry focal point.
            </Typography>

            <Divider />

            <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={2}>
                <Link href="#" variant="caption" color="text.secondary" underline="hover">
                  Privacy Policy
                </Link>
                <Link href="#" variant="caption" color="text.secondary" underline="hover">
                  Terms of Use
                </Link>
                <Link href="#" variant="caption" color="text.secondary" underline="hover">
                  Help
                </Link>
              </Stack>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                v2.0
              </Typography>
            </Stack>

            <Typography
              variant="caption"
              sx={{ color: 'text.disabled', display: { md: 'none' } }}
            >
              © {year} Government of Sarawak · MEITD
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </>
  );
}
